import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsActivity, AtsSuggestion, AtsTask, STATUS_BADGE, fmtDate, timeAgo } from './api';

export const StatusBadge: React.FC<{ value: string }> = ({ value }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ring-inset capitalize ${STATUS_BADGE[value] || 'bg-gray-100 text-gray-600 ring-gray-500/20'}`}>
    {value.replace(/_/g, ' ')}
  </span>
);

export const Modal: React.FC<{ title: string; onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({ title, onClose, wide, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className={`bg-white rounded-xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[85vh] flex flex-col`}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="px-5 py-4 overflow-y-auto">{children}</div>
    </div>
  </div>
);

export const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

export const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';
export const btnPrimary = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';
export const btnGhost = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100';

const ACTIVITY_ICONS: Record<string, string> = {
  note: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  call: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  meeting: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  stage_move: 'M13 7l5 5m0 0l-5 5m5-5H6',
  system: 'M13 10V3L4 14h7v7l9-11h-7z',
};

export const ActivityTimeline: React.FC<{ activities: AtsActivity[] }> = ({ activities }) => {
  if (!activities.length) return <p className="text-sm text-gray-400 py-4 text-center">No activity yet.</p>;
  return (
    <div className="space-y-3">
      {activities.map((a) => (
        <div key={a.id} className="flex gap-3">
          <span className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${a.type === 'stage_move' ? 'bg-violet-50 text-violet-600' : a.type === 'system' ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-600'}`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={ACTIVITY_ICONS[a.type] || ACTIVITY_ICONS.note} />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{a.body || a.type}</p>
            <p className="text-[11px] text-gray-400 mt-0.5 capitalize">{a.type.replace(/_/g, ' ')} · {timeAgo(a.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// One suggested follow-up from Jev, pending accept/dismiss.
export const SuggestionCard: React.FC<{ s: AtsSuggestion; onDone: (id: number) => void }> = ({ s, onDone }) => {
  const { token } = useAuth();
  const [due, setDue] = useState(s.due_at ? s.due_at.slice(0, 10) : '');
  const [busy, setBusy] = useState(false);

  const isDealMove = s.kind === 'deal_stage';

  const accept = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      await atsFetch(token, `/suggestions/${s.id}/accept`, {
        method: 'POST',
        body: JSON.stringify(isDealMove ? {} : { dueAt: due ? new Date(due).toISOString() : null }),
      });
      onDone(s.id);
      window.dispatchEvent(new Event('ats:tasks-changed'));
    } catch { /* leave card for retry */ }
    setBusy(false);
  };

  const dismiss = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      await atsFetch(token, `/suggestions/${s.id}/dismiss`, { method: 'POST' });
      onDone(s.id);
    } catch { /* leave card */ }
    setBusy(false);
  };

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/60 px-3.5 py-3 mb-3">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0013 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-violet-600 uppercase tracking-wide">{isDealMove ? 'Suggested deal move' : 'Suggested follow-up'}</p>
          <p className="text-sm font-medium text-gray-900 mt-0.5">{s.title}</p>
          {s.source_text && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">“{s.source_text}”</p>}
          <div className="flex items-center gap-2 mt-2">
            {!isDealMove && <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="border border-violet-200 bg-white rounded-md px-2 py-1 text-xs text-gray-600" />}
            <button onClick={accept} disabled={busy} className="px-2.5 py-1 rounded-md bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50">{isDealMove ? 'Apply move' : 'Add task'}</button>
            <button onClick={dismiss} disabled={busy} className="px-2 py-1 rounded-md text-xs text-gray-400 hover:text-gray-600">Dismiss</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Note composer + timeline, self-fetching for one entity. Logging a note runs
// Jev over the text — suggested follow-ups appear as cards above the feed.
export const TimelinePanel: React.FC<{ entityType: string; entityId: number; initial?: AtsActivity[] }> = ({ entityType, entityId, initial }) => {
  const { token } = useAuth();
  const [activities, setActivities] = useState<AtsActivity[]>(initial || []);
  const [suggestions, setSuggestions] = useState<AtsSuggestion[]>([]);
  const [body, setBody] = useState('');
  const [type, setType] = useState('note');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    atsFetch(token, `/activities?entityType=${entityType}&entityId=${entityId}`)
      .then((d) => setActivities(d.activities))
      .catch(() => {});
  }, [token, entityType, entityId]);

  useEffect(() => { if (!initial) load(); }, [load, initial]);

  useEffect(() => {
    if (!token) return;
    atsFetch(token, `/suggestions?entityType=${entityType}&entityId=${entityId}`)
      .then((d) => setSuggestions(d.suggestions))
      .catch(() => {});
  }, [token, entityType, entityId]);

  const add = async () => {
    if (!token || !body.trim()) return;
    setSaving(true);
    try {
      const d = await atsFetch(token, '/activities', { method: 'POST', body: JSON.stringify({ entityType, entityId, type, body: body.trim() }) });
      setBody('');
      if (d.suggestion) setSuggestions((prev) => [d.suggestion, ...prev]);
      load();
    } catch { /* keep draft on failure */ }
    setSaving(false);
  };

  return (
    <div>
      {suggestions.map((s) => (
        <SuggestionCard key={s.id} s={s} onDone={(id) => setSuggestions((prev) => prev.filter((x) => x.id !== id))} />
      ))}
      <div className="flex gap-2 mb-4">
        <select value={type} onChange={(e) => setType(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-600">
          <option value="note">Note</option>
          <option value="call">Call</option>
          <option value="email">Email</option>
          <option value="meeting">Meeting</option>
        </select>
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); add(); } }}
          placeholder="Log a note, call, email or meeting…"
          className={inputCls}
        />
        <button onClick={add} disabled={saving || !body.trim()} className={btnPrimary}>Log</button>
      </div>
      <ActivityTimeline activities={activities} />
    </div>
  );
};

// Notes tab: the prominent way to log notes/calls/meetings on a record
// (the side-panel composer stays for quick entries). The add dialog picks
// the note type; Jev reads what you write and suggests the follow-up.
const NOTE_TYPES = [
  { key: 'note', label: 'Note' },
  { key: 'call', label: 'Call' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'email', label: 'Email' },
];

export const NotesPanel: React.FC<{ entityType: string; entityId: number }> = ({ entityType, entityId }) => {
  const { token } = useAuth();
  const [activities, setActivities] = useState<AtsActivity[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [type, setType] = useState('note');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');

  const load = useCallback(() => {
    if (!token) return;
    atsFetch(token, `/activities?entityType=${entityType}&entityId=${entityId}`)
      .then((d) => setActivities(
        (d.activities as AtsActivity[]).filter((a) =>
          ['note', 'call', 'meeting'].includes(a.type) || (a.type === 'email' && !(a.metadata && a.metadata.synced))
        )
      ))
      .catch(() => {});
  }, [token, entityType, entityId]);

  useEffect(load, [load]);

  const save = async () => {
    if (!token || !body.trim() || saving) return;
    setSaving(true);
    try {
      const d = await atsFetch(token, '/activities', { method: 'POST', body: JSON.stringify({ entityType, entityId, type, body: body.trim() }) });
      setBody(''); setShowAdd(false);
      if (d.suggestion) {
        setFlash(`Jev suggested: "${d.suggestion.title}" — accept it from the Activity panel or the ATS Overview.`);
        window.setTimeout(() => setFlash(''), 8000);
      }
      load();
    } catch { /* keep draft */ }
    setSaving(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-400">{activities.length} note{activities.length === 1 ? '' : 's'}</p>
        <button onClick={() => setShowAdd(true)} className={btnPrimary}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Add note
        </button>
      </div>
      {flash && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 mb-3">{flash}</p>}
      {activities.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">Nothing logged yet — notes, calls and meetings land here (and on the Activity panel).</p>
      ) : (
        <ActivityTimeline activities={activities} />
      )}

      {showAdd && (
        <Modal title="Add note" onClose={() => setShowAdd(false)}>
          <div className="flex gap-2 mb-3">
            {NOTE_TYPES.map((t) => (
              <button
                key={t.key}
                onClick={() => setType(t.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${type === t.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={type === 'call'
              ? 'What was said? e.g. Rang them — want to see two more CVs, chase Thursday if quiet'
              : 'Write it down — any next step you mention becomes a suggested task.'}
            rows={6}
            className={inputCls}
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-3">
            <button onClick={() => setShowAdd(false)} className={btnGhost}>Cancel</button>
            <button onClick={save} disabled={saving || !body.trim()} className={btnPrimary}>{saving ? 'Saving…' : 'Save note'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Open tasks for one entity with quick-add + complete.
export const TasksPanel: React.FC<{ entityType: string; entityId: number }> = ({ entityType, entityId }) => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState<AtsTask[]>([]);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');

  const load = useCallback(() => {
    if (!token) return;
    atsFetch(token, `/tasks?entityType=${entityType}&entityId=${entityId}`)
      .then((d) => setTasks(d.tasks)).catch(() => {});
  }, [token, entityType, entityId]);

  useEffect(load, [load]);

  // Accepted Jev suggestions create tasks outside this panel — refresh on signal.
  useEffect(() => {
    window.addEventListener('ats:tasks-changed', load);
    return () => window.removeEventListener('ats:tasks-changed', load);
  }, [load]);

  const add = async () => {
    if (!token || !title.trim()) return;
    await atsFetch(token, '/tasks', { method: 'POST', body: JSON.stringify({ title: title.trim(), dueAt: due || null, entityType, entityId }) }).catch(() => {});
    setTitle(''); setDue('');
    load();
  };

  const complete = async (id: number) => {
    if (!token) return;
    await atsFetch(token, `/tasks/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'done' }) }).catch(() => {});
    load();
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} placeholder="Add a task…" className={inputCls} />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-600" />
        <button onClick={add} disabled={!title.trim()} className={btnPrimary}>Add</button>
      </div>
      {tasks.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">No open tasks.</p>
      ) : (
        <ul className="space-y-1.5">
          {tasks.map((t) => {
            const overdue = t.due_at && new Date(t.due_at).getTime() < Date.now();
            return (
              <li key={t.id} className="flex items-center gap-2.5 group">
                <button onClick={() => complete(t.id)} title="Mark done" className="w-4.5 h-4.5 w-[18px] h-[18px] rounded-full border-2 border-gray-300 hover:border-emerald-500 hover:bg-emerald-50 flex-shrink-0" />
                <span className="text-sm text-gray-700 flex-1 truncate">{t.title}</span>
                {t.due_at && <span className={`text-[11px] ${overdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>{fmtDate(t.due_at)}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Type-to-search picker for clients (a plain dropdown dies at a few hundred
// rows). Filters as you type; if nothing matches exactly, the last option
// creates the typed name as a new client. Post-Forager this same box becomes
// a universe company lookup with manual add as the fallback.
export const ClientPicker: React.FC<{
  clients: { id: number; name: string }[];
  valueId: number | '' | null;
  onSelect: (id: number) => void;
  onCreate?: (name: string) => Promise<{ id: number; name: string } | null>;
  autoFocus?: boolean;
}> = ({ clients, valueId, onSelect, onCreate, autoFocus }) => {
  const selected = clients.find((c) => c.id === valueId) || null;
  const [text, setText] = useState(selected ? selected.name : '');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (selected) setText(selected.name); }, [selected]);

  const q = text.trim().toLowerCase();
  const matches = (q && (!selected || text !== selected.name)
    ? clients.filter((c) => c.name.toLowerCase().includes(q))
    : clients
  ).slice(0, 8);
  const exact = clients.some((c) => c.name.trim().toLowerCase() === q);

  const create = async () => {
    if (!onCreate || !text.trim() || creating) return;
    setCreating(true);
    const made = await onCreate(text.trim()).catch(() => null);
    setCreating(false);
    if (made) { onSelect(made.id); setText(made.name); setOpen(false); }
  };

  return (
    <div className="relative">
      <input
        value={text}
        autoFocus={autoFocus}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Type to find a client…"
        className={inputCls}
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto py-1">
          {matches.map((c) => (
            <button
              key={c.id}
              onMouseDown={(e) => { e.preventDefault(); onSelect(c.id); setText(c.name); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${c.id === valueId ? 'text-blue-700 font-medium' : 'text-gray-700'}`}
            >
              {c.name}
            </button>
          ))}
          {matches.length === 0 && !q && <p className="px-3 py-2 text-sm text-gray-400">No clients yet.</p>}
          {q && !exact && onCreate && (
            <button
              onMouseDown={(e) => { e.preventDefault(); create(); }}
              className="w-full text-left px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 border-t border-gray-100"
            >
              {creating ? 'Creating…' : <>+ Add “{text.trim()}” as a new client</>}
            </button>
          )}
          {q && matches.length === 0 && !onCreate && <p className="px-3 py-2 text-sm text-gray-400">No match.</p>}
        </div>
      )}
    </div>
  );
};

// Connect-your-mailbox card, shared by Inbox and Calendar (one Microsoft
// connection powers both). Connecting goes through an explicit terms step
// that says exactly what syncs. Google lands later (restricted-scope review).
const SYNC_TERMS = [
  'Sync your emails from the last 30 days, and keep syncing new mail automatically (roughly every 10 minutes). You can backfill up to 12 months later.',
  'Jev (our AI) reads message text to file noise (notifications, marketing, cold pitches) out of your way and to suggest follow-up tasks. Messages are judged, never used to train anything.',
  'Let you reply from inside MarketMapr — replies send through your own mailbox and appear in your Sent items.',
  'Read your calendar (attendees, time, duration, location) to show your meetings and prompt follow-ups after they end.',
  'Your mail and calendar are visible only to you.',
  'Disconnecting stops all syncing immediately; already-synced history stays in your workspace until you delete it.',
];

export const MailboxConnectCard: React.FC<{ configured: boolean; error?: string | null }> = ({ configured, error }) => {
  const { token } = useAuth();
  const [showTerms, setShowTerms] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState('');

  const connect = async () => {
    if (!token || starting || !accepted) return;
    setStarting(true);
    try {
      const d = await atsFetch(token, '/email/connect');
      window.location.href = d.authorizeUrl;
    } catch (e: any) { setErr(e.message); setStarting(false); }
  };

  return (
    <div className="max-w-xl">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-8 py-10 text-center">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Connect your mailbox</h2>
        <p className="text-sm text-gray-500 mb-6">
          Connect your email and calendar, and conversations and meetings sync into your
          workspace — matched to your candidates, contacts and clients automatically.
        </p>
        {configured ? (
          <button onClick={() => setShowTerms(true)} className={btnPrimary}>
            Connect an account
          </button>
        ) : (
          <p className="text-xs text-amber-600">Microsoft OAuth isn't configured on this server (MICROSOFT_CLIENT_ID/SECRET).</p>
        )}
        {(err || error) && <p className="text-xs text-red-600 mt-3">{err || error}</p>}
      </div>

      {showTerms && (
        <Modal title="Connect your email & calendar" onClose={() => setShowTerms(false)}>
          <p className="text-sm text-gray-700 mb-3">With your account connected, MarketMapr will:</p>
          <ul className="space-y-2 mb-4">
            {SYNC_TERMS.map((t, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-gray-600">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-5 cursor-pointer">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="rounded border-gray-300" />
            I have read and accept the terms
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={connect}
              disabled={!accepted || starting}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-800 hover:border-blue-300 hover:bg-blue-50/40 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" viewBox="0 0 23 23"><rect x="1" y="1" width="10" height="10" fill="#F25022" /><rect x="12" y="1" width="10" height="10" fill="#7FBA00" /><rect x="1" y="12" width="10" height="10" fill="#00A4EF" /><rect x="12" y="12" width="10" height="10" fill="#FFB900" /></svg>
              {starting ? 'Redirecting…' : 'Microsoft / Outlook'}
            </button>
            <button
              disabled
              title="Gmail / Google Workspace support is coming later"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-100 text-sm font-medium text-gray-300 cursor-not-allowed"
            >
              <svg className="w-4 h-4 opacity-40" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 01-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" /><path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.9-3a7.2 7.2 0 01-10.8-3.8H1.3v3.1A12 12 0 0012 24z" /><path fill="#FBBC05" d="M5.2 14.3a7.2 7.2 0 010-4.6V6.6H1.3a12 12 0 000 10.8l3.9-3.1z" /><path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8L20 3.2A12 12 0 001.3 6.6l3.9 3.1A7.2 7.2 0 0112 4.8z" /></svg>
              Google <span className="text-[9px] font-bold uppercase">Soon</span>
            </button>
          </div>
          {err && <p className="text-xs text-red-600 mt-3">{err}</p>}
        </Modal>
      )}
    </div>
  );
};

export const SectionCard: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      {action}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);
